// TODO(969c-followup): `useDiagnosisCodeSearch` here is a boundary-respecting
// COPY of `@/patients/services/use-diagnosis-code-search` (billing can't
// cross-import a domain). Drift risk: a fix to one won't reach the other.
// Consolidate into a single `@/shared/` hook and have both domains consume it.
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { get, put } from '@/shared/api/api-client';

/**
 * Diagnosis add/remove for the Review Claim popup (PROD-969c phase 2).
 *
 * Diagnoses are NOT a billing record — they live on the encounter **Assessment
 * & Plan** (Flutter `UpdateDiagnosisEvent → createApUseCase`). These endpoints
 * are under `/api/Patients` + `/api/Master`, not `/api/Billing`. See
 * `docs/CONVERSIONS/claim-969c/CONVERSION_NOTES.md` for the captured wire shapes.
 *
 * ⚠️ DATA-INTEGRITY SAFEGUARD (mandatory): the A&P PUT carries the patient's
 * full clinical record — every condition's `childMedicalCode` holds its
 * medications / eRx / procedures. A diagnosis edit MUST start from the CURRENT
 * full A&P (`useEncounterAssessmentPlan`, sourced from GetAllEncounterDetails
 * which includes meds) and mutate ONLY `assessmentPlanCondition[]`, passing
 * everything else through verbatim. Reconstructing the A&P — or round-tripping
 * the invoice-embedded copy whose meds are empty — WIPES medications.
 */

// ─── ICD-10 search ────────────────────────────────────────────────────────────

/** Normalised ICD-10 option (Master endpoint ships PascalCase `Id`/`System`). */
export interface DiagnosisCodeOption {
  id: string;
  code: string;
  display: string;
  system: string;
}

interface RawDiagnosisCode {
  Id?: string;
  code?: {
    code?: string;
    display?: string;
    System?: string | null;
    Definition?: string | null;
  };
}

const MIN_QUERY_LENGTH = 2;

/**
 * ICD-10 typeahead. `GET /api/Master/DiagnosisCodes?system=ICD-10&searchQuery=`.
 * Returns PascalCase rows (`Id`/`System`); normalised here at the hook boundary
 * to `DiagnosisCodeOption`.
 *
 * NOTE: a twin lives at `@/patients/services/use-diagnosis-code-search` (returns
 * the raw `ConditionCode[]` instead). Billing can't import across domains, so
 * this is a boundary-respecting copy. Follow-up: promote one shared ICD-10
 * search hook to `@/shared/` and have both domains consume it.
 */
export function useDiagnosisCodeSearch(query: string, enabled: boolean) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ['billing-diagnosis-codes', 'ICD-10', trimmed] as const,
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams({ system: 'ICD-10', searchQuery: trimmed });
      const raw = await get<RawDiagnosisCode[]>(
        `/api/Master/DiagnosisCodes?${qs.toString()}`,
        { signal },
      );
      return (raw ?? [])
        .filter((r) => r.code?.code)
        .map<DiagnosisCodeOption>((r) => ({
          id: r.Id ?? r.code!.code!,
          code: r.code!.code!,
          display: r.code?.display ?? r.code!.code!,
          system: r.code?.System ?? 'ICD-10',
        }));
    },
    // Empty query returns the default ICD-10 list (so the Add Diagnosis modal
    // shows options the moment it opens — Flutter parity). A 1-char query is
    // still skipped (too noisy); ≥2 chars filters.
    enabled: enabled && (trimmed.length === 0 || trimmed.length >= MIN_QUERY_LENGTH),
    staleTime: 5 * 60_000,
    placeholderData: (prev) => prev,
  });
}

// ─── Encounter Assessment & Plan (read with medications) ──────────────────────

/**
 * One A&P condition. `childMedicalCode` is preserved verbatim on round-trip
 * (it holds the condition's medications / procedures) — typed `unknown` so a
 * Dx edit never reshapes it. Extra wire fields pass through via the index sig.
 */
export interface AssessmentPlanCondition {
  identifier?: string | null;
  encounter?: string | null;
  parentId?: string | null;
  isPrimary?: boolean | null;
  childMedicalCode?: unknown;
  conditionCode?: {
    code?: string;
    display?: string;
    system?: string | null;
    [k: string]: unknown;
  };
  externalCode?: unknown;
  assessmentPlanId?: string | null;
  subject?: string | null;
  performer?: string | null;
  notes?: string | null;
  onSetDate?: string | null;
  endDate?: string | null;
  isDeleted?: boolean | null;
  [k: string]: unknown;
}

/**
 * Full A&P object. Only `assessmentPlanCondition` is ever mutated; everything
 * else (meds/procedures/snomed/vaccination/radiology/referrals/flags) is an
 * opaque pass-through preserved verbatim on save.
 */
export interface EncounterAssessmentPlan {
  identifier?: string;
  encounter?: string;
  subject?: string;
  performer?: string;
  assessmentPlanCondition?: AssessmentPlanCondition[];
  [k: string]: unknown;
}

interface AllEncounterDetailsResponse {
  assessmentResponse?: EncounterAssessmentPlan[];
}

/**
 * The CURRENT full A&P for an encounter — the safe round-trip source (its
 * conditions carry their medications). `GET /api/Patients/GetAllEncounterDetails`.
 */
export function useEncounterAssessmentPlan(params: {
  encounterId?: string;
  patientId?: string;
  enabled?: boolean;
}) {
  const { encounterId, patientId, enabled = true } = params;
  return useQuery({
    queryKey: ['encounter-assessment-plan', encounterId, patientId] as const,
    queryFn: async ({ signal }) => {
      const data = await get<AllEncounterDetailsResponse>(
        `/api/Patients/GetAllEncounterDetails?Id=${encounterId}&patientId=${patientId}`,
        { signal },
      );
      return data?.assessmentResponse?.[0];
    },
    enabled: enabled && Boolean(encounterId) && Boolean(patientId),
  });
}

// ─── A&P update (PUT — full round-trip) ───────────────────────────────────────

interface UpdateAssessmentPlanVars {
  /** The CURRENT full A&P (from useEncounterAssessmentPlan) with ONLY
   *  `assessmentPlanCondition` mutated. Everything else passes through. */
  assessmentPlan: EncounterAssessmentPlan;
  /**
   * `updateType` for the A&P write. Flutter `review_claim_popup.dart:2596`:
   * `'PreClaim'` when the popup opened from the Submission flow (cameFrom==0),
   * `''` otherwise (resubmission / error). The caller passes the popup's
   * computed value — do NOT hardcode `'PreClaim'` (wrong for the
   * Remitted + Correction resubmission path).
   */
  updateType: string;
  /** Invoice id to invalidate so the popup's diagnosis list refreshes. */
  invoiceId?: string;
}

/**
 * Persist the mutated A&P. `PUT /api/Patients/Assessment?Id={identifier}`.
 * `updateType` is supplied by the caller (Submission→`'PreClaim'`, else `''`).
 * Invalidates the invoice detail + the encounter A&P cache on success.
 */
export function useUpdateAssessmentPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assessmentPlan, updateType }: UpdateAssessmentPlanVars) =>
      put(
        `/api/Patients/Assessment?Id=${assessmentPlan.identifier}`,
        { ...assessmentPlan, updateType },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['invoice'] });
      qc.invalidateQueries({ queryKey: ['encounter-assessment-plan'] });
      if (vars.invoiceId) {
        qc.invalidateQueries({ queryKey: ['invoice', 'detail', vars.invoiceId] });
      }
    },
  });
}
